/**
 * Private Layout Component
 *
 * Wrapper layout for authenticated sections of the application.
 * Includes navigation, header, and main content area.
 *
 * @module components/layout/PrivateLayout
 */

import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, NavDropdown, Button } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getUnreadCounts } from '../../services/chatService';
import AgentTriggerButton from './AgentTriggerButton';
import NotificationBell from '../notifications/NotificationBell';
import { APP_VERSION } from '../../utils/appVersion';
import { getNavItemsForRole } from '../../config/navConfig';
import GlobalSearch from '../search/GlobalSearch';
import { useGlobalSearchShortcut } from '../../hooks/useGlobalSearchShortcut';
import './PrivateLayout.css';

const PrivateLayout = () => {
  const { user, profile, isAuthenticated, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Sidebar visible on desktop by default, collapsed on mobile/tablet (< 992px)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 992);
  // Mismo umbral que el CSS (992px). Estaban desalineados: el JS colapsaba a
  // <992 pero el CSS solo sacaba el sidebar de la pagina a <=768, asi que
  // entre 769 y 991 el sidebar quedaba como riel de 80px comiendose el ancho
  // de una tablet, con los rotulos ocultos y sin forma de recuperarlos.
  const [isOverlaySidebar, setIsOverlaySidebar] = useState(window.innerWidth < 992);

  // El navbar es position: fixed, asi que .main-content tiene que reservar su
  // altura o el contenido queda debajo. Esa reserva estaba hardcodeada en 60px
  // (el min-height del navbar) mientras el navbar renderiza 83-89px segun el
  // ancho — el logo, el buscador y el avatar lo estiran. La diferencia se venia
  // tapando con un padding-top: 100px puesto en global.css "para evitar
  // superposiciones con el header": el mismo trabajo hecho dos veces, y de mas.
  // Resultado medido: 160px reservados para un navbar de 83, o sea 77px de
  // espacio muerto en TODAS las pantallas privadas.
  //
  // Se mide la altura real y se publica como variable CSS. Un numero fijo es
  // exactamente como nacio este bug; si manana el navbar crece, la reserva
  // crece con el.
  useEffect(() => {
    // Se busca el nodo por clase en vez de por ref: <Navbar> de react-bootstrap
    // no reenvia el ref al DOM, asi que navbarRef.current quedaba en null y el
    // observer nunca corria. El sintoma era silencioso — la variable CSS no se
    // publicaba y todo seguia andando por el valor de respaldo.
    // El componente hace `if (loading) return <Spinner/>` mas abajo, asi que en
    // el primer commit el navbar todavia no existe. Con deps [] el efecto corria
    // una sola vez, contra un DOM sin navbar, salia por aca y no volvia a
    // correr nunca: la variable no se publicaba y todo seguia andando por el
    // valor de respaldo del CSS. Por eso depende de loading/isAuthenticated.
    const el = document.querySelector('.private-navbar');
    if (!el) return undefined;
    const publish = () => {
      document.documentElement.style.setProperty('--private-navbar-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, isAuthenticated]);

  // Notification count now comes from NotificationContext via NotificationBell

  // Global Search / Command Palette (issue #419) — ⌘K/Ctrl+K works from any
  // private screen because PrivateLayout is the single wrapper every private
  // route renders (see App.js). The header affordance button lives just
  // below, next to the other navbar icon actions.
  const { isOpen: isSearchOpen, open: openSearch, close: closeSearch } = useGlobalSearchShortcut();

  // Total unread messages across all conversations (for sidebar badge)
  const { data: chatUnreadCounts = {} } = useQuery({
    queryKey: ['conversations-unread'],
    queryFn: async () => {
      const { data } = await getUnreadCounts();
      return data || {};
    },
    enabled: !!user,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 15,
  });

  const totalChatUnread = React.useMemo(
    () => Object.values(chatUnreadCounts).reduce((sum, n) => sum + (n || 0), 0),
    [chatUnreadCounts]
  );

  // Handle window resize to show/hide sidebar appropriately
  useEffect(() => {
    const handleResize = () => {
      const overlay = window.innerWidth < 992;
      setIsOverlaySidebar(overlay);
      setSidebarCollapsed(overlay);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abierto en telefono el sidebar TAPA la pagina, asi que necesita las dos
  // salidas que cualquier overlay tiene: tocar afuera (el backdrop de abajo)
  // y Escape. Sin ellas el unico modo de cerrarlo era volver a apretar la
  // hamburguesa, que queda detras del propio panel.
  useEffect(() => {
    if (!isOverlaySidebar || sidebarCollapsed) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSidebarCollapsed(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOverlaySidebar, sidebarCollapsed]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the current path
  // so Login.js can restore it after successful sign-in.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect technicians to their dedicated portal, except when visiting shared
  // routes that are role-adaptive (help manual, my-tools desktop view,
  // warehouse self-service pickup, and the tool-request inbox from #105).
  const technicianAllowedPaths = [
    '/ayuda',
    // Tu agente (conexion + handbook): lo ven los cuatro roles.
    '/agente',
    '/mis-herramientas',
    '/bodega',
    '/herramientas/mis-solicitudes',
    // Líder inbox (issue #131) — technicians need access to this even though
    // their default landing is the technician portal.
    '/mis-cotizaciones',
    // Editing an assigned quotation: technicians who are also Líder must be
    // able to open the QuotationBuilder for a quotation they own. RLS still
    // governs which rows they can actually load/update.
    '/cotizaciones',
    // Unified calendar (#494/#490): a technician's default view is their own
    // agenda, with an explicit opt-in to see the team's — that decision is
    // dead code if the redirect below never lets them land on the page.
    '/calendario'
  ];
  if (
    profile?.role === 'technician'
    && !technicianAllowedPaths.some(p => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/tecnico/dashboard" replace />;
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Close sidebar on navigation click (mobile/tablet only, < 992px)
  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      setSidebarCollapsed(true);
    }
  };

  return (
    <div className="private-layout">
      {/* Top Navigation Bar */}
      <Navbar bg="white" className="shadow-sm border-bottom private-navbar" sticky="top">
        <Container fluid>
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="sidebar-toggle me-3"
              onClick={toggleSidebar}
              aria-label="Abrir menú de navegación"
              aria-expanded={!sidebarCollapsed}
            >
              <i className="bi bi-list"></i>
            </Button>
            <img
              src={`${process.env.PUBLIC_URL}/images/logos/Logo_PROMEDIC-07.png`}
              alt="ProMedic"
              className="navbar-logo"
            />
          </div>

          {/* Global Search / Command Palette (#419).
              A real, always-visible search bar owns the middle of the header
              from `md` up so the feature is discoverable without knowing the
              shortcut; below `md` it collapses into a 44x44 icon button that
              sits with the other navbar icons. Ctrl+K / ⌘K keeps working
              everywhere via useGlobalSearchShortcut. */}
          <div className="global-search-trigger-wrap">
            <button
              type="button"
              className="global-search-trigger"
              onClick={openSearch}
              aria-label="Buscar en ProMedic (Ctrl + K)"
              title="Buscar en ProMedic (Ctrl + K)"
              data-testid="global-search-trigger"
            >
              <i className="bi bi-search global-search-trigger-icon" aria-hidden="true"></i>
              <span className="global-search-trigger-label">
                Buscar proyectos, tareas, herramientas…
              </span>
              <span className="global-search-trigger-kbd" aria-hidden="true">
                <kbd>Ctrl</kbd>
                <kbd>K</kbd>
              </span>
            </button>
          </div>

          <Nav className="align-items-center">
            {/* Tu agente — lleva a /agente (conectarse por Kane OneDeck o ChatGPT y ver el handbook). El popup SSO a Kane vive ahora en esa pantalla (useKaneAssistant).
                Es LA puerta de entrada al agente, o sea la via principal del
                producto, y era un boton de solo icono cuyo unico nombre era un
                `title`: los title no se muestran nunca en touch y un lector de
                pantalla los trata como texto opcional. aria-label lo nombra de
                verdad; el title se queda como tooltip de escritorio. */}
            <AgentTriggerButton />

            {/* Notifications */}
            <NotificationBell />

            {/* User Avatar Dropdown */}
            <NavDropdown
              title={
                <span className="user-avatar-dropdown">
                  <span className="user-avatar-circle">
                    {profile?.full_name
                      ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                      : user?.email
                        ? user.email.charAt(0).toUpperCase()
                        : <i className="bi bi-person-fill"></i>}
                  </span>
                  <span className="user-avatar-name">{profile?.full_name || user?.email}</span>
                </span>
              }
              align="end"
            >
              <NavDropdown.Item as={NavLink} to="/perfil">
                <i className="bi bi-person me-2"></i>
                Mi Perfil
              </NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/agente">
                <i className="bi bi-robot me-2"></i>
                Tu agente
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>
                Cerrar Sesión
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>

      <div className="d-flex">
        {/* Backdrop del sidebar en modo overlay (< 992px). Es un <button> y no
            un <div onClick> a proposito: asi trae foco, Enter/Espacio y un
            nombre accesible sin inventarle handlers a mano. */}
        {isOverlaySidebar && !sidebarCollapsed && (
          <button
            type="button"
            className="sidebar-backdrop"
            onClick={() => setSidebarCollapsed(true)}
            aria-label="Cerrar menú de navegación"
            data-testid="sidebar-backdrop"
          />
        )}

        {/* Sidebar — items are derived from a role-aware nav config (issue #132).
            Each item declares which roles can see it; getNavItemsForRole filters
            the full list down to what the current profile is allowed to see.
            The /mensajes badge is the only dynamic decoration and is rendered
            inline below. */}
        <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <Nav className="flex-column sidebar-nav">
            {getNavItemsForRole(profile?.role).map((item) => (
              <React.Fragment key={item.path}>
                {item.section && !sidebarCollapsed && (
                  <div className="sidebar-section-label">{item.section}</div>
                )}
                <Nav.Link
                  as={NavLink}
                  to={item.path}
                  className="sidebar-link"
                  onClick={handleNavClick}
                  title={item.title || item.label}
                  end={item.path === '/dashboard'}
                >
                  <i className={`bi ${item.icon} me-2`}></i>
                  {!sidebarCollapsed && <span>{item.label}</span>}
                  {/* Unread badge — only the Mensajes item carries a counter for now. */}
                  {item.path === '/mensajes' && !sidebarCollapsed && totalChatUnread > 0 && (
                    <span className="sidebar-link-badge">
                      {totalChatUnread > 99 ? '99+' : totalChatUnread}
                    </span>
                  )}
                </Nav.Link>
              </React.Fragment>
            ))}
          </Nav>

          <div className="sidebar-footer">
            <NavLink
              to="/novedades"
              onClick={handleNavClick}
              className="sidebar-version-badge"
              title={`ProMedic v${APP_VERSION} — Ver novedades`}
            >
              <i className="bi bi-tag-fill me-2" aria-hidden="true"></i>
              {!sidebarCollapsed && <span>v{APP_VERSION}</span>}
            </NavLink>
          </div>
        </div>

        {/* Main Content */}
        <main className={`main-content flex-fill ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Container fluid className="py-4">
            <Outlet />
          </Container>
        </main>
      </div>

      <GlobalSearch show={isSearchOpen} onHide={closeSearch} />
    </div>
  );
};

export default PrivateLayout;
